import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { SignIn, SignUp } from "@clerk/clerk-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Shared styling so <SignIn> and <SignUp> render identically inside the
// existing Vyom-branded glass shell -- no separate design system, no
// Clerk-hosted chrome (header/footer hidden, card made transparent).
const CLERK_APPEARANCE = {
  variables: {
    colorPrimary: "#5B4DFF",
    colorBackground: "transparent",
    colorText: "#e2e8f0",
    colorInputBackground: "rgba(255,255,255,0.05)",
    colorInputText: "#ffffff",
    borderRadius: "0.75rem",
  },
  elements: {
    // cardBox/card ship a fixed ~400px width + 40px padding in Clerk's own
    // default theme, which (a) overflowed past the "Director Access" glass
    // panel's actual content width and (b) sat indented ~40px further right
    // than the "Enter the studio" heading above it -- that mismatch was the
    // alignment issue. w-full/max-w-full/p-0 make the card fill exactly the
    // same content box as the heading, edge to edge, nothing else changed.
    rootBox: "w-full",
    cardBox: "w-full max-w-full shadow-none",
    card: "w-full max-w-full bg-transparent shadow-none p-0",
    header: "hidden",
    headerTitle: "hidden",
    headerSubtitle: "hidden",
    footer: "hidden",
  },
};

// Floating cinematic dust motes -- identical to the approved shell in
// pages/Login.jsx. Duplicated here (not imported) so that frozen legacy
// file is never touched by this integration.
function Motes({ reduced }) {
  const motes = Array.from({ length: reduced ? 0 : 26 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {motes.map((_, i) => {
        const size = 1 + (i % 4);
        const left = (i * 37) % 100;
        const top = (i * 53) % 100;
        const dur = 9 + (i % 7) * 2;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full bg-white/40"
            style={{ width: size, height: size, left: `${left}%`, top: `${top}%` }}
            animate={{ y: [0, -26, 0], opacity: [0.15, 0.6, 0.15] }}
            transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        );
      })}
    </div>
  );
}

// Clerk-powered sign-in / sign-up (Google and/or email, per what's enabled
// in the Clerk Dashboard), mounted INSIDE the same cinematic shell used by
// the legacy pages/Login.jsx -- hero/parallax background, dust motes,
// floating film-frames, headline, and the "Director Access" console --
// so Clerk augments the approved Vyom experience instead of replacing it.
// Mode toggles via ?mode=signup on the SAME /login route, no new route.
export default function ClerkLogin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode") === "signup" ? "signup" : "signin";
  const reduced = useReducedMotion();
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const onMove = (e) => {
    if (reduced) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPointer({ x: (e.clientX / r.width - 0.5) * 2, y: (e.clientY / r.height - 0.5) * 2 });
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen w-full overflow-hidden bg-[#07070D] text-white grid lg:grid-cols-[1.15fr_0.85fr]"
      data-testid="clerk-login-page"
    >
      {/* Living cinematic environment (full-bleed parallax hero) -- identical to pages/Login.jsx */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: "url('/brand/login_hero.jpg')" }}
          animate={reduced ? {} : { x: pointer.x * -18, y: pointer.y * -12 }}
          transition={{ type: "spring", stiffness: 40, damping: 20 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07070D] via-[#07070D]/25 to-[#07070D]/92" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070D] via-transparent to-[#07070D]/40" />
        <div className="absolute -left-40 top-1/3 w-[560px] h-[560px] rounded-full bg-[#5B4DFF]/20 blur-[140px]" />
        <Motes reduced={reduced} />

        <div className="pointer-events-none absolute inset-0 hidden lg:block" aria-hidden>
          {[
            { top: "6%", right: "12%", w: 200, rot: -6, d: 0 },
            { top: "74%", right: "15%", w: 172, rot: 4, d: 1.2 },
            { top: "40%", right: "1%", w: 128, rot: -3, d: 0.6 },
          ].map((f, i) => (
            <motion.div
              key={i}
              className="absolute rounded-xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]"
              style={{ top: f.top, right: f.right, width: f.w, rotate: `${f.rot}deg` }}
              animate={reduced ? {} : { y: [0, -14, 0], opacity: [0.5, 0.85, 0.5] }}
              transition={{ duration: 10 + i * 2, repeat: Infinity, ease: "easeInOut", delay: f.d }}
            >
              <img src="/brand/ambient_world.jpg" alt="" className="w-full h-24 object-cover" draggable={false} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 hidden lg:flex h-full flex-col justify-between p-12">
          <Logo height={30} />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-lg"
          >
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#7C70FF] mb-4">
              AI Film Studio
            </p>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05]">
              Step into the<br />director's chair.
            </h1>
            <p className="mt-5 text-base text-slate-300/90 max-w-md leading-relaxed">
              Ideas become stories. Stories become worlds. Worlds become films —
              rendered inside your own cinematic universe.
            </p>
          </motion.div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Idea → Story → Scenes → Film
          </div>
        </div>
      </div>

      {/* Director login console (anchored right-center, framed by film frames) */}
      <div className="absolute z-20 inset-y-0 right-0 w-full lg:w-[44%] flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo height={30} />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-7 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)]">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C70FF] mb-1.5">
              Director Access
            </p>
            <h2 className="font-display text-2xl font-bold mb-6">Enter the studio</h2>

            <div className="flex justify-center" data-testid="clerk-signin">
              {CLERK_ENABLED ? (
                mode === "signup" ? (
                  <SignUp
                    key="signup"
                    routing="hash"
                    signInUrl="/login?mode=signin"
                    fallbackRedirectUrl="/"
                    appearance={CLERK_APPEARANCE}
                  />
                ) : (
                  <SignIn
                    key="signin"
                    routing="hash"
                    signUpUrl="/login?mode=signup"
                    fallbackRedirectUrl="/"
                    appearance={CLERK_APPEARANCE}
                  />
                )
              ) : (
                <p className="text-sm text-slate-400 text-center">
                  Clerk is not configured. Set <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> to enable Google sign-in.
                </p>
              )}
            </div>

            {CLERK_ENABLED && (
              <button
                type="button"
                data-testid="auth-mode-toggle"
                onClick={() => navigate(mode === "signup" ? "/login?mode=signin" : "/login?mode=signup")}
                className="block w-full text-center text-xs text-slate-400 hover:text-[#7C70FF] transition mt-5"
              >
                {mode === "signup" ? "Already have an account? Sign in" : "New to Vyom? Create an account"}
              </button>
            )}

            <p className="text-center text-xs text-slate-500 mt-6">
              Private studio · Secure sign-in with Google
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
